import { MouseEvent } from 'react'
import { useHref, useLinkClickHandler } from 'react-router-dom'
import { isMenuActive } from './isMenuActive'

/**
 * Generates a click handler for navigation links using React Router's `useLinkClickHandler`.
 *
 * This handler will trigger a custom `onClick` callback if provided, then delegate navigation to React Router.
 *
 * @param {string} to - The target path to navigate to.
 * @param {() => void} [onClick] - Optional callback to run before navigation.
 * @returns {onClick: (e: MouseEvent<HTMLAnchorElement>) => void, href, 'aria-current' } A click handler
 * suitable for use on anchor elements.
 */
export const useNavigationHandler = (to: string = '', onClick?: () => void) => {
  const href = useHref(to)
  const routerClick = useLinkClickHandler(to, { replace: false })

  const onNavigateClick = (e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    e.preventDefault()
    onClick?.()

    // Close any open popover menus when a navigation item is clicked
    const popover = e.currentTarget.closest('[role="menu"]') as HTMLElement & {
      hidePopover?: () => void
    }
    popover?.hidePopover?.()

    /**
     * Handles a click event on an anchor tag to perform client-side routing.
     * @param {MouseEvent} e The click event object.
     */
    if ((e.currentTarget as HTMLElement).tagName === 'A') {
      routerClick(e as MouseEvent<HTMLAnchorElement>)
    }
  }

  return {
    'aria-current': isMenuActive(href),
    href,
    onClick: onNavigateClick,
  }
}
